import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { apiClient } from '../App';
import { CheckCircle, Package } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

const OrderSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await apiClient.get(`/checkout/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        return;
      } else if (response.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling
      setStatus('processing');
      setTimeout(() => {
        setAttempts(attempts + 1);
        pollPaymentStatus();
      }, pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-12">
      <div className="container mx-auto px-8 md:px-16 lg:px-24">
        <div className="max-w-2xl mx-auto text-center" data-testid="order-success-container">
          {status === 'checking' || status === 'processing' ? (
            <div data-testid="payment-processing-state">
              <div className="mb-6">
                <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary mx-auto"></div>
              </div>
              <h1 className="text-3xl font-medium mb-4 text-foreground">Processing Payment...</h1>
              <p className="text-slate-400">Please wait while we confirm your payment.</p>
            </div>
          ) : status === 'success' ? (
            <div data-testid="payment-success-state">
              <CheckCircle size={64} weight="fill" className="text-green-500 mx-auto mb-6" />
              <h1 className="text-3xl font-medium mb-4 text-foreground">Order Confirmed!</h1>
              <p className="text-slate-300 mb-8">
                Thank you for your order. We'll send you an email confirmation shortly.
              </p>
              <div className="flex gap-4 justify-center">
                <Link to="/orders">
                  <Button className="rounded-sm" data-testid="view-orders-button">
                    <Package size={20} weight="regular" className="mr-2" />
                    View Orders
                  </Button>
                </Link>
                <Link to="/products">
                  <Button variant="outline" className="rounded-sm" data-testid="continue-shopping-button">
                    Continue Shopping
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            <div data-testid="payment-error-state">
              <h1 className="text-3xl font-medium mb-4 text-foreground">Payment Issue</h1>
              <p className="text-slate-400 mb-8">
                There was an issue confirming your payment. Please check your email or contact support.
              </p>
              <Link to="/orders">
                <Button className="rounded-sm" data-testid="check-orders-button">
                  Check My Orders
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderSuccessPage;
